// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import cstmTable from '../../../share/components/vue/PaginationTable';
import config from '../../../share/config';
import { wizardComponents } from './componentsMap';
import individualDiagnose from '../../../share/util/individualDiagnose';
import api from '../../../common/api';
import alert from '../../../share/alert';
import loading from '../../../share/components/vue/ProcessingIcon';
import util from '../../../share/util/utils';
import eventLogUtil from '../../../share/util/eventLogUtil';
import constants from '../../../common/nodejs/constants';
import DiagSystemUtils from '../../../common/nodejs/diagSystemUtils';
import { getAbbrSysName } from '../../../share/util/systemName';

// 特殊キーのenum
const SPWS_KEY_LIST = {
  CTRL_ALT_J: 0,
  CTRL_ALT_P: 1,
  CTRL_ALT_U: 2,
  CTRL_ALT_D: 3,
  CTRL_ALT_M: 4
};
// 特殊キーに対応するcode
const SPWS_KEY_CODE = [{
  key: SPWS_KEY_LIST.CTRL_ALT_J,
  code: [17, 18, 74]
}, {
  key: SPWS_KEY_LIST.CTRL_ALT_P,
  code: [17, 18, 80]
}, {
  key: SPWS_KEY_LIST.CTRL_ALT_U,
  code: [17, 18, 85]
}, {
  key: SPWS_KEY_LIST.CTRL_ALT_D,
  code: [17, 18, 68]
}, {
  key: SPWS_KEY_LIST.CTRL_ALT_M,
  code: [17, 18, 77]
}];

// 押されたキーコードの配列
let keyCode = [];

export default {
  name: "individualWorkSupport",
  data() {
    return {
      processing: false,
      currentWizard: '',
      // APIから取得した機能リストを常に保持しておく
      originalFunctionList: [],
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      // 選択中の機能オブジェクト
      selectedFunction: {},
      // フィルター用テキスト
      filterText: '',
      // システム間移動判定
      isUseCache: true,
      // WS用のi18nKey
      i18nWsKey: '',
      menuResponse: null,
      targetSpKey: '',
      immutableIdList: [],
      tmpIdList: [],
      unsupportedKeyList: [],
      viewedIdList: []
    };
  },
  props: {
    // noop
  },
  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    ...wizardComponents
  },
  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.menuResponse) {
          return;
        }
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const MenuList = this.getWsi18nMenuList(this.menuResponse, systemId);
        this.filterFunctionList = MenuList;
        this.originalFunctionList = MenuList;
        this.updateFilter();
      }
    }
  },
  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      isPopstateNavigation: state => state.cache.isPopstateNavigation,
    }),
    systemTableColumns() {
      return [
        { name: 'menu_title', label: 'individual.header_work_support_table_item' },
      ];
    }
  },
  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      vm.beforeRouteProcess(vm, from);
    });
  },
  /**
   * 遷移してきた画面のパラメータ情報を保持
   * 同一コンポーネントが再帰的に利用された場合、呼び出される
   */
  beforeRouteUpdate(to, from, next) {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      next();
      return;
    }
    this.$nextTick(() => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      this.beforeRouteProcess(this, from);
      // テーブル情報クリア（本当は mountedLogic に入れたいが、影響範囲を考慮してここにピンポイントで入れている）
      this.originalFunctionList = [];
      this.filterFunctionList = [];
      // mounted時に行う共通処理を呼び出す
      this.mountedLogic();
    });
    next();
  },
  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),
    ...mapActions('cache', ['cacheIsPopstateNavigation']),

    /** componentのmounted時に呼び出す処理 */
    mountedLogic() {
      this.updateTable();
    },
    /** 選択した機能を変数に保持 */
    clickRow: function (selected) {
      this.selectedFunction = selected;
      this.setHighLight();
    },
    /** 選択している機能のハイライト設定 */
    setHighLight() {
      this.filterFunctionList.forEach(item => {
        if (item.work_support_id === this.selectedFunction.work_support_id && item.requirement_id === this.selectedFunction.requirement_id) {
          item.fieldSpec = {};
          item.fieldSpec.menu_title = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
          }
        }
      });
    },
    /** 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする */
    updateFilter() {
      this.selectedFunction = {};
      this.setHighLight();
      this.filterFunctionList = this.originalFunctionList.filter(
        // 翻訳後の名称を大文字・小文字区別なくフィルターする
        item => item.menu_title.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },
    /** サポート機能選択時 */
    async onClickSelect() {
      // 選択中の機能がない場合、return
      if (!this.selectedFunction.menu_title) {
        return;
      }
      // システムIDの取得
      const systemId = individualDiagnose.getCurrentSystemId(this);
      // イベントログの操作 ID 発行
      this.processing = true;
      await eventLogUtil.createEventLogHandleId(this.netAppId);
      this.processing = false;

      let componentName;
      // requirement_id 名をコンポーネント名に変換, WS用のi18nKeyの取得
      if (this.immutableIdList.includes(this.selectedFunction.work_support_id) && this.selectedFunction.isSpecial) {
        this.i18nWsKey = `spws${systemId}.${this.selectedFunction.work_support_id}`;
        componentName = `requirementSp${this.selectedFunction.requirement_id}`;
      } else {
        this.i18nWsKey = `ws${systemId}.${this.selectedFunction.work_support_id}`;
        // requirement_id が3桁以下の場合0埋めする
        // requirement_id が4桁以上の場合でも正常にcomponentNameを取得できるようにpadStartの3桁で0埋めをする
        componentName = `requirement${(this.selectedFunction.requirement_id).toString().padStart(3, "0")}`;
      }
      this.currentWizard = componentName;

      // 作業サポート各機能の利用回数カウントアップ
      this.usageCountUp({ type: config.USAGE_COUNT_WSP, systemName: systemId, immutableId: this.selectedFunction.work_support_id });
      // イベントログのページビューカウント
      eventLogUtil.eventLogPageViewCount(constants.EVENT_LOG_PAGE_VIEW_PAGE_NAME_INDIVIDUAL_WSP);
    },
    /** ウィザードを閉じた後の処理 */
    closeWizard() {
      this.currentWizard = '';
    },
    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
    /** テーブル情報の更新を行う */
    async updateTable() {
      try {
        this.processing = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        // 作業サポート一覧取得のポーリング処理を行う
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.WSP,
              '/netApps/' + this.netAppId + '/systems/' + systemId + '/initialize',
              {
                use_cache: this.isUseCache
              }
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.WSP,
              '/netApps/' + this.netAppId + '/systems/' + systemId + '/initialize/requests/' + postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse && postApiResponse.data && postApiResponse.data.request_id ? '' : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (getApiResponse && getApiResponse.data && getApiResponse.data.status) {
              switch (getApiResponse.data.status) {
                case config.STATUS_WSP_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_WSP_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_WSP_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          // ポーリングが正常に完了した場合は、一覧データ取得処理を行う
          this.menuResponse = await this.getMenuResponse(this.netAppId, systemId);
          // responseが存在しない場合、処理終了
          if (!this.menuResponse) {
            return;
          }
          // 作業サポート一覧の多言語変換リストの取得
          const MenuList = this.getWsi18nMenuList(this.menuResponse, systemId);
          this.filterFunctionList = MenuList;
          this.originalFunctionList = MenuList;
          this.updateFilter();
          // 表示項目がない場合、非サポート画面を表示
          if (this.filterFunctionList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
      }
    },
    /**
     * 一覧取得処理を行う関数
     * @param {string} netAppId クラウドコネクタ ID
     * @param {string} systemId システム ID
     * @returns 成功時は menus リストを返す（失敗時は null）
     */
    async getMenuResponse(netAppId, systemId) {
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await api.getCall(
            config.WSP,
            '/netApps/' + netAppId + '/systems/' + systemId + '/menus'
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // menus が入っていれば成功とする
          return getApiResponse && getApiResponse.data && getApiResponse.data.menus ? '' : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
      // 処理失敗時は response が null になるので考慮
      return response ? response.data.menus : null;
    },
    /** その他のエラー */
    showUnexpectedError() {
      alert.showError(this, null, this.$t('individual.error_Initialize_communication_error'), () => { });
    },
    /**
     * 作業サポートの多言語変換の一覧取得
     * @param {Array} menuResponse メニュー一覧
     * @param {string} systemId システム ID
     */
    getWsi18nMenuList(menuResponse, systemId) {
      const isPowerSupply = DiagSystemUtils.isPowerSupplyByAbbreviationId(getAbbrSysName(systemId));
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * @param {string} i18nWsMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nWsMenuKey, messageKey, work_support_id = 0, special_requirement_id = '') => {
        const key = `${i18nWsMenuKey}.${messageKey}`;
        // 翻訳キーとメッセージキーの組み合わせから文言が見つかった場合、翻訳語文言を返却する
        if (this.$te(key)) {
          return this.$t(key);
        }
        // 作業サポートIDと機能要求IDが環境変数に設定された"COM1"のIDである場合、COM1のタイトルを返却する
        if (messageKey === 'menu_title' && work_support_id === Number(process.env.VUE_APP_COM1_WORK_SUPPORT_ID) && special_requirement_id === process.env.VUE_APP_COM1_SPECIAL_REQUIREMENT_ID) {
          return this.$t('individual.menu_title_COM1');
        }
        return undefined;
      };

      // 多言語変換リストの取得
      return menuResponse.map(item => {
        let i18nWsMenuKey;
        if (item.special_requirement_id && this.immutableIdList.includes(item.work_support_id)) {
          i18nWsMenuKey = `spws${systemId}.${item.work_support_id}`;
          return {
            work_support_id: item.work_support_id,
            sort_id: item.sort_id,
            requirement_id: item.special_requirement_id,
            isSpecial: true,
            // menu_title が見つからなかった場合は undefined のままにする
            // 特殊キー入力で表示される機能は先頭に「*」を付与
            menu_title: '*' + convertI18nText(i18nWsMenuKey, 'menu_title', item.work_support_id, item.special_requirement_id),
            // menu_description が見つからないケースは「説明なし」なので空にする
            // ※作業サポート一覧の仕様上、普通にあり得る
            menu_description: convertI18nText(i18nWsMenuKey, 'menu_description') || '',
          };
        } else {
          i18nWsMenuKey = `ws${systemId}.${item.work_support_id}`;
          return {
            work_support_id: item.work_support_id,
            sort_id: item.sort_id,
            requirement_id: item.requirement_id,
            isSpecial: false,
            // menu_title が見つからなかった場合は undefined のままにする
            // Phase4.Step1 ではECU品番、VIN登録、クーラント注水制御モードのみしか文言定義していないので、
            // 多くが undefined になるが、Phase4.Step2 では undefined は発生しない想定
            menu_title: convertI18nText(i18nWsMenuKey, 'menu_title', item.work_support_id, item.special_requirement_id),
            // menu_description が見つからないケースは「説明なし」なので空にする
            // ※作業サポート一覧の仕様上、普通にあり得る
            menu_description: convertI18nText(i18nWsMenuKey, 'menu_description') || '',
            additional_info: item.additional_info,
          };
        }
      }).filter(
        // Phase4.Step1 では暫定で、menu_title の有/無（翻訳の有/無）で判断する
        // ECU品番、VIN登録、クーラント注水制御モードがテスト対象であり、それ以外をフィルタするため
        // Phase4.Step2 では、不要な処理という認識
        (item) => item.menu_title
      ).filter(
        // キーレスアクセス＆プッシュスタート(電源)でWS102が表示されないようにする
        (item) => {
          if (isPowerSupply && item.requirement_id === 102) return false;
          return true;
        }
      ).sort((a, b) => {
        // sort_id(表示順) 要素でソートする
        return a.sort_id - b.sort_id;
      });
    },
    /**
     * 呼び出し時のコンポーネント内ガードの共通処理
     * @param vm 呼び出し元のオブジェクト(this)
     * @param from 遷移元 
     */
    beforeRouteProcess(vm, from) {
      if (util.isFromSettingsPage(from.name) || util.isSameSystemPage(from)) {
        // 単位設定画面またはパスワード変更画面からの遷移時、または同じシステム内の子機能間への遷移の場合 trueを指定
        vm.isUseCache = true;
      } else {
        // 上記以外の場合、falseを指定
        vm.isUseCache = false;
      }
    },
    /**
     * キーが離されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyUp(event) {
      const i = keyCode.indexOf(event.keyCode);
      // 配列にキーコードがあればその要素を削除
      if (i !== -1) { keyCode.splice(i, 1); }
      if (keyCode.length === 3) { this.inputKey(); }
    },
    /**
     * キーが押されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyDown(event) {
      // 配列に値がない、または処理中でなければ
      if (-1 === keyCode.indexOf(event.keyCode) && this.currentWizard !== 'hiddenFunction' && !this.processing) {
        keyCode.push(event.keyCode); // キーコードを追加
        if (keyCode.length === 3) { this.inputKey(); }
      }
    },
    /** 特殊キーチェック */
    inputKey() {
      const keyPushed = SPWS_KEY_CODE.find(obj => JSON.stringify(obj.code) === JSON.stringify(keyCode.sort((a, b) => { return a - b; })));
      if (typeof keyPushed !== 'undefined' && !this.unsupportedKeyList.includes(keyPushed.key)) {
        if (!this.viewedIdList.includes(keyPushed.key)) {
          // 指定されたキーが同時に押下されていて、未サポート一覧/表示済みリストに存在しない
          this.targetSpKey = keyPushed.key;
          this.currentWizard = 'hiddenFunction';
        }
      }
    },
    /**
     * 特殊キー押下による特殊機能メニューの取得
     * @param {Array} menuList 特殊機能含むメニューリスト
     * @param {Array} getMenuIdList 取得済みの特殊機能メニューリスト
     */
    getSpMenu(menuList, getMenuIdList) {
      this.immutableIdList = getMenuIdList;
      const systemId = individualDiagnose.getCurrentSystemId(this);
      // menuListが存在しない場合、処理終了
      if (!menuList) {
        return;
      }
      // メニューサポートデータ取得
      if (this.menuResponse !== null && this.menuResponse.length === menuList.length) {
        // 未サポートのキーを控える
        this.unsupportedKeyList.push(this.targetSpKey);
        // 未サポート画面表示し処理終了する
        const messages = this.$t('individual.unsupported_spws');
        alert.showInfo(this, null, messages);
      } else {
        // 実行したキーを控える
        this.viewedIdList.push(this.targetSpKey);
        this.menuResponse = menuList;
        const MenuList = this.getWsi18nMenuList(this.menuResponse, systemId);
        this.filterFunctionList = MenuList;
        this.originalFunctionList = MenuList;
        this.updateFilter();
      }
      // キーコード初期化
      keyCode = [];
    },
  },
  created() {
    // noop
  },
  mounted() {
    this.mountedLogic();
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    this.unsupportedKeyList = [];
    this.viewedIdList = [];
  },
};
